import { Button, ButtonGroup, Link, Spinner } from "@mixitone/components";
import CloseIcon from "@mixitone/icons/close.svg";
import { ApplicationView } from "@mixitone/mvc";
import clsx from "clsx";
import { AppController } from "controllers/AppController";
import MixitoneController from "controllers/MixitoneController";
import dayjs from "dayjs";
import { isTestMode } from "lib/environment";
import tauri from "lib/tauri";
import { useCallback } from "react";

interface State {
  displayNotification?: boolean;
}

const CHECK_INTERVAL = 1000 * 60;

class OfflineNotificationController extends MixitoneController<State> {
  get initialState(): State {
    return {
      displayNotification: false,
    };
  }

  remindAtCheckInterval?: ReturnType<typeof setInterval>;

  override async initialize() {
    this.remindAtCheckInterval = setInterval(() => {
      this.updateDisplayNotification();
    }, CHECK_INTERVAL);
    this.addDependency(() => {
      clearInterval(this.remindAtCheckInterval);
    });

    this.observe(() => {
      this.updateDisplayNotification();
    });
  }

  updateDisplayNotification() {
    if (isTestMode()) {
      this.state.displayNotification = false;
      return;
    }

    const app = this.get(AppController);
    const debugUi =
      !!app.state.updateDevMock && app.state.updatePhase !== "idle" && app.state.updatePhase !== undefined;

    const remindAtFromStorage = localStorage.getItem("remindAt");
    if (remindAtFromStorage) {
      const remindAtInFuture = dayjs().isBefore(dayjs(remindAtFromStorage));
      if (remindAtInFuture && !debugUi) {
        this.state.displayNotification = false;
        return;
      }
    }

    this.state.displayNotification =
      tauri.enabled && (!app.state.online || (app.state.online && !!app.state.updateAvailable) || debugUi);
  }

  actionRemindMeLater() {
    localStorage.setItem("remindAt", dayjs().add(1, "day").toISOString());
    this.updateDisplayNotification();
  }
}

const OfflineNotification: React.FC = () => {
  const controller = OfflineNotificationController.use();
  const appController = AppController.use();

  const { displayNotification } = controller.state;
  const {
    updateAvailable,
    updateDevMock,
    updatePhase,
    updateDownloadProgress,
    updateError,
    online,
    updateVersion,
  } = appController.state;

  const handleRemindMeLater = useCallback(() => {
    controller.actionRemindMeLater();
  }, [controller]);

  const handleInstall = useCallback(() => {
    appController.actionInstallAndRestart();
  }, [appController]);

  const handleRetry = useCallback(() => {
    appController.actionRetryAutoDownload();
  }, [appController]);

  const handleCheckUpdates = useCallback(() => {
    appController.actionCheckForUpdates();
  }, [appController]);

  const showUpdateCard = online && updateAvailable;
  const showDebugCard = !!updateDevMock && updatePhase !== "idle" && updatePhase !== undefined;

  return (
    <div
      data-update-toast
      className={clsx(
        "dark fixed bottom-4 right-4 z-40 max-w-sm w-80 rounded-xl bg-slate-800 p-4 text-white shadow-xl",
        "transition-opacity duration-300",
        {
          "opacity-0 pointer-events-none": !displayNotification,
          "opacity-100": displayNotification,
        },
      )}
    >
      {!online && (
        <button type="button" className="absolute right-3 top-3">
          <CloseIcon
            onClick={handleRemindMeLater}
            className="fill-slate-400 transition-transform hover:scale-125 hover:fill-slate-200 @sm/app:w-[22px]"
          />
        </button>
      )}

      {!online && (
        <div className="flex flex-col w-full gap-2 pr-6">
          <h1 className="font-bold text-lg">Ready to sync?</h1>
          <p className="text-sm">
            Your recent gameplay stats and player updates are waiting to be sent online.
          </p>
          <p className="text-sm">Connect to the internet soon to keep everything up to date!</p>
          <div className="flex justify-center mt-3">
            <Link target="_blank" href={`${import.meta.env.VITE_WWW}/docs/game-sessions/sessions`}>
              Read more
            </Link>
          </div>
        </div>
      )}

      {online && (showUpdateCard || showDebugCard) && (
        <div className="flex flex-col w-full gap-3">
          <h2 className="font-semibold text-base">Mixitone update</h2>
          {updateVersion && (
            <p className="text-xs text-slate-400">
              New version: <span className="text-slate-200">{updateVersion}</span>
            </p>
          )}

          {updatePhase === "idle" && (
            <div className="flex items-center gap-2 text-sm">
              <Spinner size={20} className="fill-white shrink-0" />
              <span>Preparing download…</span>
            </div>
          )}

          {updatePhase === "downloading" && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 text-sm">
                <Spinner size={20} className="fill-white shrink-0" />
                <span>Downloading update…</span>
                {updateDownloadProgress > 0 && (
                  <span className="tabular-nums">{updateDownloadProgress}%</span>
                )}
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded bg-slate-700">
                <div
                  className="h-full rounded bg-brand-600 transition-[width]"
                  style={{ width: `${Math.min(100, updateDownloadProgress)}%` }}
                />
              </div>
            </div>
          )}

          {updatePhase === "ready" && (
            <div className="flex flex-col gap-3 mt-1">
              <Button type="button" className="w-full justify-center" onClick={handleInstall}>
                Install & restart
              </Button>
            </div>
          )}

          {updatePhase === "installing" && (
            <div className="flex items-center gap-2 text-sm">
              <Spinner size={24} className="fill-white shrink-0" />
              <span>Installing…</span>
            </div>
          )}

          {updatePhase === "error" && updateError && (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-red-300">{updateError}</p>
              <ButtonGroup className="flex-wrap">
                <Button type="button" onClick={handleRetry}>
                  Retry
                </Button>
                <Button kind="secondary" type="button" onClick={handleCheckUpdates}>
                  Check again
                </Button>
              </ButtonGroup>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OfflineNotificationController.scope(ApplicationView(OfflineNotification));
